import { Close } from '@mui/icons-material';
import {
  Box,
  Button,
  IconButton,
  Modal,
  Typography,
} from '@mui/material';
import React, { useState } from 'react';
import { useIntl } from 'react-intl';
import { useDispatch, useSelector } from 'react-redux';
import { useHistory } from 'react-router-dom';
import { addKitToUser } from '@services/rest/creabots';
import { getCreabotsKits, setSelectedKit } from '../features/creabotsSlice';
import { Kits } from '../features/kits/index.ts';
import { KitCard } from './ui/kitCard';
import kitOptions from '../constants';


export const AddKitModal = ({ isOpen, handleCloseModal }) => {
  const intl = useIntl();
  const userKitList = useSelector((state) => state.creabots.kits.list);
  const dispatch = useDispatch();
  const history = useHistory();
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState('');
  const filteredKits = kitOptions.map((kit) => {
    const alreadyAdded = userKitList.length ? userKitList.some((k) => k.id === kit.id) : null;
    if (alreadyAdded) {
      return {
        ...kit,
        isDisabled: true,
      };
    }
    return kit;
  });
  const [isSelected, setIsSelected] = useState(2);

  const handleSelected = (kitId) => {
    setIsSelected(kitId);
  };

  const handleAddKitToUser = async () => {
    try {
      setIsLoading(true);
      await addKitToUser({ creabotsKitId: isSelected });

      const selectedKitToAdd = kitOptions.find(kit => kit.id === isSelected);

      await dispatch(getCreabotsKits(true));

      if (selectedKitToAdd) {
        dispatch(setSelectedKit(selectedKitToAdd));
        const selectedKitToRedirect = Object.values(Kits).find((kit) => kit.id === isSelected);
        history.push(`/kits/creabots/${selectedKitToRedirect.urlName}`);
      }

      setError('');
      setIsSelected(2);
      handleCloseModal();
    } catch (err) {
      setError(
        err.response?.data?.message
        || intl.formatMessage({ id: 'creabots.dashboard.addKitModal.error' }),
      );
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <Modal
      open={isOpen}
      onClose={handleCloseModal}
      aria-labelledby="Kit Modal"
      aria-describedby="Modal para agregar kits"
      disableEscapeKeyDown={isLoading}
    >
      <Box
        sx={(theme) => ({
          backgroundColor: theme.palette.common.white,
          borderRadius: theme.spacing(2),
          display: 'flex',
          flexDirection: 'column',
          position: 'absolute',
          top: '50%',
          left: '50%',
          transform: 'translate(-50%, -50%)',
          width: 'min(90vw, 930px)',
          maxWidth: '930px',
          maxHeight: '90vh',
          minHeight: '5%',
          height: 'fit-content',
          overflow: 'auto',
        })}
      >
        <Box
          sx={(theme) => ({
            paddingX: 'clamp(1rem, 3vw, 1.5rem)',
            paddingY: 'clamp(0.75rem, 2vw, 1rem)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
          })}
        >
          <Typography
            sx={(theme) => ({
              fontWeight: 500,
              color: theme.palette.text.primary,
              letterSpacing: '-0.015rem',
              fontSize: 'clamp(1rem, 2.5vw, 1.125rem)',
              lineHeight: 'clamp(20px, 3vw, 24px)',
              fontFamily: theme.typography.fontFamily,
            })}
          >
            {intl.formatMessage({ id: 'creabots.dashboard.sidebar.addKit' })}
          </Typography>
          <IconButton
            sx={{ padding: 0 }}
            disabled={isLoading}
            onClick={handleCloseModal}
          >
            <Close
              sx={(theme) => ({
                height: '24px',
                width: '24px',
                color: theme.palette.text.secondary,
              })}
            />
          </IconButton>
        </Box>
        <Box
          sx={(theme) => ({
            width: '100%',
            height: '100%',
            padding: 'clamp(1rem, 3vw, 1.5rem)',
            display: 'grid',
            gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))',
            gap: 'clamp(0.75rem, 2vw, 1.2rem)',
          })}
        >
          {filteredKits.map((kit) => (
            <Box key={kit.name}>
              <KitCard
                kit={kit}
                isSelected={isSelected === kit.id}
                setIsSelected={handleSelected}
              />
            </Box>
          ))}
        </Box>
        <Box
          display="flex"
          alignItems="center"
          width="100%"
          justifyContent={!error ? 'end' : 'space-between'}
          sx={{
            paddingX: 'clamp(1rem, 3vw, 2rem)',
            paddingBottom: 'clamp(1rem, 2vw, 1.5rem)',
            flexWrap: 'wrap',
            gap: 'clamp(0.5rem, 2vw, 1rem)',
          }}
        >
          {error && (
            <Typography
              sx={(theme) => ({
                fontFamily: theme.typography.fontFamily,
                fontSize: '0.875rem',
                color: theme.palette.error.main,
                fontWeight: theme.typography.fontWeightRegular,
              })}
            >
              {error}
            </Typography>
          )}
          <Box
            display="flex"
            alignItems="center"
            sx={{
              gap: 'clamp(0.75rem, 2vw, 1.5rem)',
              paddingTop: 'clamp(0.5rem, 2vw, 1rem)',
              paddingLeft: 'clamp(0.5rem, 2vw, 1rem)',
              minWidth: 'fit-content',
            }}
          >
            <Button
              size="small"
              variant="secondary"
              onClick={handleCloseModal}
              disabled={isLoading || filteredKits.length === userKitList.length}
              sx={{
                minWidth: 'clamp(80px, 15vw, 120px)',
                padding: 'clamp(0.25rem, 1vw, 0.5rem) clamp(0.75rem, 3vw, 1.5rem)',
              }}
            >
              {intl.formatMessage({ id: 'common.cancel' })}
            </Button>
            <Button
              variant="primary"
              size="small"
              onClick={handleAddKitToUser}
              disabled={isLoading || filteredKits.length === userKitList.length}
              sx={{
                minWidth: 'clamp(80px, 15vw, 120px)',
                padding: 'clamp(0.25rem, 1vw, 0.5rem) clamp(0.75rem, 3vw, 1.5rem)',
              }}
            >
              {intl.formatMessage({ id: 'common.confirm' })}
            </Button>
          </Box>
        </Box>
      </Box>
    </Modal>
  );
};


